import { cn } from "@/lib/utils";
import { Link, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

/**
 * Project sub-nav. Vite-native rewrite of the Next `ProjectTabs`.
 *
 * Tabs whose Vite route is ported (`ported: true`) use a typed
 * `<Link>`; the rest are plain `<a href>` that full-navigate to
 * the still-Next-owned `/projects/:slug/...` pages — the same
 * "unported destination → full nav" pattern used across the
 * migration. Flip `ported` as each sub-route lands.
 */

type Tab = {
  /** Key under `projectTabs` in the translations. */
  label: string;
  /** URL segment under `/projects/:slug/`. "" = overview. */
  segment: string;
  /** True once the Vite route exists — renders a typed `<Link>`. */
  ported?: boolean;
};

const TABS: Tab[] = [
  { label: "overview", segment: "", ported: true },
  { label: "work-items", segment: "work-items", ported: true },
  { label: "releases", segment: "releases", ported: true },
  { label: "time-entries", segment: "time-entries", ported: true },
  { label: "members", segment: "members", ported: true },
  { label: "wiki", segment: "wiki", ported: true },
  { label: "files", segment: "files", ported: true },
  { label: "keys", segment: "keys", ported: true },
  { label: "suggestions", segment: "suggestions", ported: true },
  { label: "planner", segment: "planner", ported: true },
  { label: "settings", segment: "settings", ported: true },
];

const TAB_CLASS =
  "inline-flex items-center px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap";
const ACTIVE = "border-primary text-primary";
const INACTIVE =
  "border-transparent text-muted-foreground hover:text-foreground hover:border-border";

export function ProjectTabs({
  slug,
  activeSegment,
  canEdit,
  useReleases,
  isCustomer,
  hasFiles,
  hasKeys,
}: {
  slug: string;
  /**
   * Force a tab active by segment instead of deriving it from the
   * URL. Used by pages that sit under a project but aren't a
   * project sub-route (work-item detail → "work-items").
   */
  activeSegment?: string | undefined;
  /**
   * Viewer manages this project (or is a Hub admin). Hides the Edit
   * tab when explicitly `false`; undefined keeps it (callers without
   * the flag, e.g. the work-item detail header). Suggestions is
   * stricter — it needs an explicit `true`.
   */
  canEdit?: boolean | undefined;
  /** Hide the Releases tab when the project has releases turned off. */
  useReleases?: boolean | undefined;
  /** Customer account — only Work Items and Wiki are visible. */
  isCustomer?: boolean;
  /** The viewer may see the project's Seafile folder. */
  hasFiles?: boolean | undefined;
  /** The viewer may see the project's Vault collections. */
  hasKeys?: boolean | undefined;
}) {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const base = `/projects/${slug}`;

  const visibleTabs = TABS.filter((t) => {
    if (isCustomer) {
      // Customer sees only Work Items and Wiki — Overview hides
      // the team / time / release widgets so it's not useful, and
      // Members / Releases / Time Entries / Settings are all
      // team-internal surfaces.
      return t.segment === "work-items" || t.segment === "wiki";
    }
    if (t.segment === "settings" && canEdit === false) return false;
    // Suggestions is a manager surface: it decides what Hubert proposed for
    // this project and can send him into a call. Same right as Edit — but
    // unlike Edit, an undefined flag hides it, because a caller that doesn't
    // know whether the viewer manages the project mustn't advertise it.
    if (t.segment === "suggestions" && canEdit !== true) return false;
    // Planner is the same shape of right as Suggestions: talking to Radim lets
    // an agent change this project's tickets under the viewer's name.
    if (t.segment === "planner" && canEdit !== true) return false;
    if (t.segment === "releases" && useReleases === false) return false;
    if (t.segment === "files" && hasFiles !== true) return false;
    if (t.segment === "keys" && hasKeys !== true) return false;
    return true;
  });

  return (
    <nav
      className="border-b border-border mt-4 mb-8 -mx-4 px-4 lg:-mx-6 lg:px-6"
      data-testid="project-tabs"
    >
      <ul className="flex gap-0 overflow-x-auto">
        {visibleTabs.map(({ label: key, segment, ported }) => {
          const label = t(`projectTabs.${key}`);
          const href = segment ? `${base}/${segment}` : base;
          const isActive =
            activeSegment !== undefined
              ? segment === activeSegment
              : segment
                ? pathname.startsWith(href)
                : pathname === base;
          const className = cn(TAB_CLASS, isActive ? ACTIVE : INACTIVE);
          return (
            <li key={key}>
              {!ported ? (
                // Sub-route still on Next — full navigation.
                <a href={href} className={className}>
                  {label}
                </a>
              ) : segment === "work-items" ? (
                <Link to="/projects/$slug/work-items" params={{ slug }} className={className}>
                  {label}
                </Link>
              ) : segment === "time-entries" ? (
                <Link to="/projects/$slug/time-entries" params={{ slug }} className={className}>
                  {label}
                </Link>
              ) : segment === "members" ? (
                <Link to="/projects/$slug/members" params={{ slug }} className={className}>
                  {label}
                </Link>
              ) : segment === "wiki" ? (
                <Link to="/projects/$slug/wiki" params={{ slug }} className={className}>
                  {label}
                </Link>
              ) : segment === "releases" ? (
                <Link to="/projects/$slug/releases" params={{ slug }} className={className}>
                  {label}
                </Link>
              ) : segment === "suggestions" ? (
                <Link to="/projects/$slug/suggestions" params={{ slug }} className={className}>
                  {label}
                </Link>
              ) : segment === "planner" ? (
                <Link to="/projects/$slug/planner" params={{ slug }} className={className}>
                  {label}
                </Link>
              ) : segment === "files" ? (
                <Link
                  to="/projects/$slug/files"
                  params={{ slug }}
                  search={{ path: "" }}
                  className={className}
                >
                  {label}
                </Link>
              ) : segment === "keys" ? (
                <Link to="/projects/$slug/keys" params={{ slug }} className={className}>
                  {label}
                </Link>
              ) : segment === "settings" ? (
                <Link to="/projects/$slug/settings" params={{ slug }} className={className}>
                  {label}
                </Link>
              ) : (
                <Link to="/projects/$slug" params={{ slug }} className={className}>
                  {label}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
